import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle } from 'lucide-react';
import { ContactForm } from '../types';
import analytics from '../utils/analytics';
import { usePageTitle } from '../hooks/usePageTitle';

const ContactPage: React.FC = () => {
  // 设置页面标题和描述
  usePageTitle(
    '联系我们',
    '期待与您的合作，期待为您提供最合适最优质的技术及设备解决方案。联系电话：+86 17896504267，邮箱：surekiln@163.com'
  );

  const [formData, setFormData] = useState<ContactForm>({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    productInterest: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // 页面加载埋点
  useEffect(() => {
    analytics.contact.viewContactPage();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSubmitting(false);
    setIsSubmitted(true);
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: '',
        productInterest: ''
      });
    }, 3000);
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: '公司地址',
      content: '佛山市禅城区澜石（国际）金属交易中心第十三座二层36号铺',
      description: '',
      type: 'address' as const
    },
    {
      icon: Phone,
      title: '联系电话(微信同号)',
      content: '+86 17896504267',
      description: '周一至周五 9:00-18:00',
      type: 'phone' as const,
      href: 'tel:+8617896504267'
    },
    {
      icon: Mail,
      title: '邮箱地址',
      content: 'surekiln@163.com',
      description: '24小时内回复',
      type: 'email' as const,
      href: 'mailto:surekiln@163.com'
    },
    {
      icon: Clock,
      title: '工作时间',
      content: '周一至周五 9:00-18:00',
      description: '周六 9:00-12:00',
      type: 'time' as const
    }
  ];

  const productInterests = [
    '瓷砖整线装备',
    '日用瓷整线装备',
    '卫生陶瓷整线装备',
    '特种窑炉及整线',
    '节能技术咨询',
    '其他'
  ];

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-primary">
        <div className="container-max text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              联系我们
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              期待与您的合作，期待为您提供最合适最优质的技术及设备解决方案
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              联系信息
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              多种方式联系我们，我们随时为您提供专业服务
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="card text-center p-6"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
                  <info.icon className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{info.title}</h3>
                {info.href ? (
                  <a
                    href={info.href}
                    onClick={() => {
                      if (info.type === 'phone') {
                        analytics.contact.clickPhone(info.content);
                      } else if (info.type === 'email') {
                        analytics.contact.clickEmail(info.content);
                      }
                    }}
                    className="text-primary-600 hover:text-primary-700 font-medium mb-2 inline-block transition-colors"
                  >
                    {info.content}
                  </a>
                ) : (
                  <p className="text-gray-900 font-medium mb-2">{info.content}</p>
                )}
                <p className="text-gray-600 text-sm">{info.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="section-padding bg-gray-50">
        <div className="container-max">
          <div className="max-w-4xl mx-auto">
            {/* Map and Additional Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              {/* Additional Info */}
              <div className="card p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">为什么选择我们</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-gray-900">专业团队</h4>
                      <p className="text-sm text-gray-600">多年行业经验的专业技术团队</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-gray-900">技术先进</h4>
                      <p className="text-sm text-gray-600">采用先进的燃烧加热及节能技术</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-gray-900">服务及时</h4>
                      <p className="text-sm text-gray-600">快速响应，提供专业的技术支持</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-gray-900">品质保证</h4>
                      <p className="text-sm text-gray-600">严格的质量控制，确保产品品质</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
